import { MailOpen, PenSquare } from "lucide-react";
import { useTranslate } from "ra-core";
import { useCallback, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";

import { ComposeEmailDialog, type EmailDraft } from "../ComposeEmailDialog";
import type { MailAccount } from "../mailTypes";
import { useMailAccount } from "../useMailAccount";
import { FolderList } from "./FolderList";
import { MessageList } from "./MessageList";
import { MessageView } from "./MessageView";
import { useMailboxFolders } from "./useMailbox";

/** « Messagerie »: the user's own mailbox, read live from Infomaniak. */
export const MailboxPage = () => {
  const translate = useTranslate();
  const { data: account, isPending } = useMailAccount();
  if (isPending) return null;
  if (account?.mail_status !== "connected" || !account.email) {
    return (
      <Card className="mt-4 max-w-xl">
        <CardContent className="flex flex-col items-start gap-4">
          <p className="text-sm">{translate("crm.mailbox.not_connected")}</p>
          <Button asChild>
            <Link to="/mail-settings">
              {translate("crm.mail.compose.connect")}
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }
  return <Mailbox account={account as MailAccount & { email: string }} />;
};

MailboxPage.path = "/mailbox";

const Mailbox = ({ account }: { account: MailAccount & { email: string } }) => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const [draft, setDraft] = useState<EmailDraft | null>(null);
  const { data: folders = [] } = useMailboxFolders();

  const folder = params.get("folder") ?? "INBOX";
  const uid = Number(params.get("uid")) || null;
  const q = params.get("q") ?? "";
  const page = Number(params.get("page")) || 1;
  const current = folders.find((item) => item.path === folder);

  const update = useCallback(
    (changes: Record<string, string | null>) =>
      setParams((previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(changes)) {
          if (value == null || value === "") next.delete(key);
          else next.set(key, value);
        }
        return next;
      }),
    [setParams],
  );
  const search = useCallback(
    (value: string) => update({ q: value, page: null, uid: null }),
    [update],
  );

  const showList = !isMobile || uid == null;
  const showMessage = !isMobile || uid != null;

  return (
    <div className="mt-2 flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="flex-1 text-xl font-semibold">
          {translate("crm.mailbox.title")}
        </h1>
        <span className="text-sm text-muted-foreground">{account.email}</span>
        <Button onClick={() => setDraft({ links: {} })}>
          <PenSquare className="size-4" />
          {translate("crm.mailbox.new")}
        </Button>
      </div>
      <div className="grid gap-4 md:grid-cols-[12rem_minmax(16rem,22rem)_minmax(0,1fr)] md:h-[calc(100vh-11rem)]">
        {showList ? (
          <FolderList
            folders={folders}
            selected={folder}
            compact={isMobile}
            onSelect={(path) =>
              update({ folder: path, uid: null, q: null, page: null })
            }
          />
        ) : null}
        {showList ? (
          <MessageList
            key={folder}
            folder={folder}
            folderKind={current?.kind ?? null}
            page={page}
            q={q}
            selectedUid={uid}
            onOpen={(next) => update({ uid: String(next) })}
            onSearch={search}
            onPage={(next) => update({ page: String(next), uid: null })}
          />
        ) : null}
        {showMessage ? (
          <div className="min-h-0 overflow-y-auto">
            {uid != null ? (
              <MessageView
                key={`${folder}-${uid}`}
                messageRef={{ folder, uid }}
                ownEmail={account.email}
                isTrash={current?.kind === "trash"}
                onCompose={setDraft}
                onDeleted={() => update({ uid: null })}
                onBack={isMobile ? () => update({ uid: null }) : undefined}
              />
            ) : (
              <div className="flex h-full min-h-40 flex-col items-center justify-center gap-2 rounded-md border border-dashed text-sm text-muted-foreground">
                <MailOpen className="size-6" />
                {translate("crm.mailbox.select")}
              </div>
            )}
          </div>
        ) : null}
      </div>
      <ComposeEmailDialog
        open={draft != null}
        onClose={() => setDraft(null)}
        draft={draft ?? { links: {} }}
      />
    </div>
  );
};
